import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { atsLab } from '../../lib/api';
import type { AtsModelInfo } from '../../lib/api';
import { Btn, C, Card, MODEL_META, ModelChip, Spinner, fmtWhen, rupees } from './ui';

// The Model Lab: every Claude model's real numbers on this team's resumes —
// cost, time, the score it added, how often the fact check had to stop it,
// and how many comparisons it won. Every resume runs on one model (Opus
// 5.5); comparisons here try the others.

type Recent = { id: string; resumeId: string; candidateName: string; jobTitle: string; company: string; models: string[]; winner: string; at: string };

const secs = (n: number | null) => (n == null ? '—' : `${Math.round(n)}s`);

const Row: React.FC<{ label: string; value: React.ReactNode; best?: boolean }> = ({ label, value, best }) => (
  <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0 text-xs">
    <span className="text-gray-500">{label}</span>
    <span className={`font-extrabold ${best ? 'text-amber-700' : 'text-gray-900'}`}>{best && '🥇 '}{value}</span>
  </div>
);

const ModelLab: React.FC<{ pw: string; onOpen: (id: string) => void }> = ({ pw, onOpen }) => {
  const [models, setModels] = useState<AtsModelInfo[]>([]);
  const [inUse, setInUse] = useState('');
  const [recent, setRecent] = useState<Recent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await atsLab(pw);
      setModels(res.models);
      setInUse(res.defaults?.make ?? '');
      setRecent(res.recent);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [pw]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size={32} /></div>;

  const tested = models.filter(m => m.make.ok > 0);
  const bestOf = (pick: (m: AtsModelInfo) => number | null, low: boolean) => {
    const vals = tested.map(pick).filter((v): v is number => v != null);
    if (new Set(vals).size < 2) return null;   // nothing to compare, or all the same
    return low ? Math.min(...vals) : Math.max(...vals);
  };
  const best = {
    wins: Math.max(0, ...models.map(m => m.wins)),
    gain: bestOf(m => m.make.gain, false),
    held: bestOf(m => m.make.held, true),
    time: bestOf(m => m.make.sec, true),
  };

  return (
    <div className="space-y-5 ats-fade">
      <div className="relative overflow-hidden rounded-3xl p-6 sm:p-7 bg-white border border-gray-100 ats-card">
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full ats-glow" style={{ background: 'radial-gradient(circle, rgba(124,58,237,.12), transparent 65%)' }} />
        <p className="relative text-xl font-black text-gray-900">🧪 Model Lab</p>
        <p className="relative text-sm text-gray-500 mt-1 max-w-2xl">Try the Claude models on real resumes and see which one is best for you. Every resume adds to these numbers.</p>
        <div className="relative grid sm:grid-cols-3 gap-3 mt-5">
          {[['1', 'Open a checked resume', 'Any resume at the Match report step.'], ['2', '⚖ Compare models', 'Pick 2 to 4 models. Each writes its own version.'], ['3', '🏆 Keep the best', 'Your pick counts as a win here.']].map(([n, t, d]) => (
            <div key={n} className="flex gap-3 rounded-2xl bg-[#faf9fc] border border-gray-100 p-3.5">
              <span className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-black text-white" style={{ background: C.purple }}>{n}</span>
              <div><p className="text-sm font-extrabold text-gray-900">{t}</p><p className="text-xs text-gray-500">{d}</p></div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {models.map(m => {
          const meta = MODEL_META[m.id];
          const okRate = m.make.runs + m.check.runs ? Math.round(((m.make.ok + m.check.ok) / (m.make.runs + m.check.runs)) * 100) : null;
          return (
            <div key={m.id} className="bg-white rounded-3xl border border-gray-100 ats-card overflow-hidden flex flex-col" data-lab-model={m.id}>
              <div className="p-5 pb-4" style={{ background: `linear-gradient(160deg, ${meta?.color}18, transparent 70%)` }}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-lg font-black" style={{ color: meta?.color }}>{meta?.icon} {m.name}</p>
                  {m.wins > 0 && m.wins === best.wins && <span className="text-[10px] font-black px-2 py-1 rounded-full" style={{ background: C.gold, color: C.purple }}>🏆 Top</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{m.blurb}</p>
                <p className="mt-3"><span className="text-2xl font-black text-gray-900">~{rupees(m.resumeInr)}</span> <span className="text-xs text-gray-500">a resume{m.estimated ? ' (est.)' : ''}</span></p>
                <p className="text-xs font-bold text-gray-700 mt-1">🏆 {m.wins} win{m.wins === 1 ? '' : 's'} of {m.compares} comparison{m.compares === 1 ? '' : 's'}</p>
                {m.id === inUse && <p className="text-[11px] font-bold text-purple-700 mt-1">✓ Used for every resume</p>}
              </div>
              <div className="px-5 pb-5 flex-1">
                <Row label="Score added (avg.)" value={m.make.gain == null ? '—' : `+${m.make.gain}%`} best={best.gain != null && m.make.gain === best.gain} />
                <Row label="Fact check stops (avg.)" value={m.make.held == null ? '—' : m.make.held} best={best.held != null && m.make.held === best.held} />
                <Row label="Time to write" value={secs(m.make.sec)} best={best.time != null && m.make.sec === best.time} />
                <Row label="Time to check" value={secs(m.check.sec)} />
                <Row label="Worked first time" value={okRate == null ? '—' : `${okRate}%`} />
                <Row label="Resumes written" value={m.make.ok} />
                <p className="text-[10px] text-gray-400 mt-2">${m.price.input} in / ${m.price.output} out per million tokens</p>
              </div>
            </div>
          );
        })}
      </div>
      {tested.length === 0 && <p className="text-xs text-gray-400 -mt-2">No resumes written yet — the numbers fill in as you use the AI. Costs marked "est." are guesses until a model has 3 runs.</p>}

      <div>
        <Card title="Latest comparisons" icon="⚖">
          {recent.length === 0 ? (
            <p className="text-sm text-gray-400">No comparisons yet. Open a checked resume and press ⚖ Compare models.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {recent.map(c => (
                <div key={c.id} className="py-3 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-gray-900 truncate">{c.candidateName || 'Candidate'} <span className="font-semibold text-gray-500">· {c.jobTitle}{c.company ? ` · ${c.company}` : ''}</span></p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {c.models.map(id => <span key={id} className={c.winner === id ? 'ring-2 ring-[#F0B429] rounded-full' : ''}><ModelChip id={id} prefix={c.winner === id ? '🏆 ' : ''} /></span>)}
                      <span className="text-[11px] text-gray-400 ml-1">{fmtWhen(c.at)}</span>
                    </div>
                  </div>
                  {c.resumeId
                    ? <Btn size="sm" onClick={() => onOpen(c.resumeId)}>{c.winner ? 'Open' : 'Pick the best →'}</Btn>
                    : <span className="text-[11px] text-gray-400">Resume deleted</span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ModelLab;
